import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authClient, parseJWT } from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { isOwner, isOwnerPortal, ROLE } from 'utils/isOwner';

interface AuthData {
    access_token: string;
    app_name: string | null;
    expires_in: number | null;
    jti: string;
    last_signon_timestamp: number | null;
    refresh_token: string;
    scope: string;
    tenant_name: string;
    token_type: string;
    user_type: string;
    uuid: string;
    need_change_password?: boolean;
}
interface AuthState {
    data: AuthData | null;
    roles?: (keyof typeof ROLE)[];
    loading?: boolean;
    error?: boolean;
    isMerchant: boolean;
}

const initialState: AuthState = {
    data: null,
    roles: [],
    loading: false,
    error: false,
    isMerchant: false,
};

type UserData = {
    [key: string]: number | undefined;
};
const totalAttempts = 10;
const updateUserAttempts = (userId: string) => {
    const userDataJSON = localStorage.getItem('userAttempts');
    let userData: UserData = {};

    if (userDataJSON) {
        userData = JSON.parse(userDataJSON) || {};
    }
    if (userData?.[userId]) {
        userData[userId] = (userData?.[userId] || 0) + 1;
    } else {
        userData[userId] = 1;
    }
    const attempts = totalAttempts - (userData?.[userId] || 1);
    if (attempts > 0) {
        notify(`You have ${totalAttempts - (userData?.[userId] || 1)} attempts left! After that your account will be locked.`, 'error');
        localStorage.setItem('userAttempts', JSON.stringify(userData));
    }
};

const removeUserAttempts = (userId: string) => {
    const userDataJSON = localStorage.getItem('userAttempts');
    let userData: UserData = {};

    if (userDataJSON) {
        userData = JSON.parse(userDataJSON);
    }
    if (!userData?.[userId]) return;

    delete userData[userId];
    localStorage.setItem('userAttempts', JSON.stringify(userData));
};

interface LoginPayload {
    username: string;
    password: string;
}

interface ChangePasswordPayload {
    currentPassword: string;
    newPassword: string;
}

export const login = createAsyncThunkWrapper<any, LoginPayload>('auth/login', async ({ username, password }: LoginPayload) => {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');
    const response:any = await authClient.post('/auth/oauth/token', params.toString());
    const { access_token, refresh_token } = response || {};
    if (!access_token) {
        // handling this because backend do not return the number of failed attempts and we want to show the user how many /failed attempts has made so he do not lock his account accidentally
        return updateUserAttempts(username);
    }
    removeUserAttempts(username);
    let authorities = parseJWT(access_token)?.['authorities'] || [];
    let roles = Array.from(authorities.values()) as (keyof typeof ROLE)[];

    if (!isOwnerPortal(roles)) {
        notify('Your are a lower role to continue.', 'error');
        return;
    }

    localStorage.setItem('token', JSON.stringify(response));
    authClient.setToken(access_token, refresh_token);
    return { response, roles };
});

export const forgotPassword = createAsyncThunkWrapper<any, LoginPayload>('auth/forgotPassword', async (username: LoginPayload) => {
    const res: any = await authClient.post('/auth/api/reset/password', { username }, { 'Content-Type': 'application/json' });
    return res;
});
export const changePassword = createAsyncThunkWrapper<any, ChangePasswordPayload>(
    'auth/changePassword',
    async ({ currentPassword, newPassword }: ChangePasswordPayload) => {
        const res: any = await authClient.post(
            '/auth/api/myinfo/changePassword',
            { currentPassword, newPassword },
            { 'Content-Type': 'application/json' }
        );
        return res;
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        updateAuthInfo(state, action: PayloadAction<AuthData>) {
            const { payload } = action || {};
            if (!payload) return;
            state.data = payload;
        },
        resetState() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(login.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(login.fulfilled, (state, action) => {
            state.loading = false;
            const { payload } = action;
            if (!payload) return;
            const { response, roles } = payload || {};
            if (!response) return;
            state.data = response;
            if (roles?.length) {
                state.roles = roles || [];
                state.isMerchant = isOwner(roles);
            }
        });
        builder.addCase(login.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    },
});

export const { updateAuthInfo, resetState } = authSlice.actions;
export default authSlice.reducer;
